import React, { FC, Fragment, useEffect, useState } from 'react'
import { Dialog } from '../../../ui'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { InfoOutlined, Warning } from '@mui/icons-material'
import {
	useRegionalPlanProducts,
	useSubscribe,
} from '../../../../hooks/api/billingHooks'
import {
	PaymentPeriodType,
	Plan,
	PlanNameType,
} from '../../../../lib/api/types/billing.interfaces'
import { CurrentUserObject } from '../../../../lib/api/types/user.interfaces'
import { ConfirmationModal } from '../../common'
import {
	parsePlanName,
	isPlanDowngrade,
	getPriceForPeriod,
	getUsersValidationError,
	getPlanMinimumUsers,
} from './utils'
import PeriodSwitcher from './components/PeriodSwitcher'
import PlanCard from './components/PlanCard'
import ModalActions from './components/ModalActions'
import cn from '../../../../lib/utils/cn'
import { useAuth } from '../../../../hooks'
import Button from '../../../ui/Button'

export interface ChoosePlanModalProps {
	isOpen: boolean
	onClose: () => void
	currentPlan?: Plan
	currentUser?: CurrentUserObject
	onActionCallback?: () => void
	onConfigurePlan?: () => void
}

export interface FormValues {
	period: PaymentPeriodType
	plan: PlanNameType
	business_users: number
	enterprise_users: number
}

const ChoosePlanModal: FC<ChoosePlanModalProps> = ({
	isOpen,
	onClose,
	currentPlan,
	currentUser,
	onActionCallback,
	onConfigurePlan,
}) => {
	const { isAuthenticated } = useAuth()
	const [
		isDowngradeSubscriptionModalOpen,
		setIsDowngradeSubscriptionModalOpen,
	] = useState(false)
	const [isOverLimitModalOpen, setIsOverLimitModalOpen] = useState(false)

	const {
		data: modalPlans,
		isLoading: isLoadingPlans,
		isError: isPricingError,
		refetch: refetchPlans,
	} = useRegionalPlanProducts({ enabled: isOpen })

	const planName = parsePlanName(currentPlan?.name ?? currentUser?.plan?.name)
	const defaultPeriod = currentPlan?.paymentPeriod
	const purchasedMaxUsers = currentUser?.plan?.maxUsers

	const getDefaultUsers = (targetPlan: 'Business' | 'Enterprise') =>
		currentPlan?.maxUsers ??
		purchasedMaxUsers ??
		getPlanMinimumUsers(
			targetPlan,
			parsePlanName(currentPlan?.name) === targetPlan
				? currentPlan?.minUsers
				: undefined,
		)

	const methods = useForm<FormValues>({
		defaultValues: {
			period: defaultPeriod ?? 'month',
			plan: planName,
			business_users: getDefaultUsers('Business'),
			enterprise_users: getDefaultUsers('Enterprise'),
		},
		reValidateMode: 'onChange',
		mode: 'onChange',
	})

	const { watch, setValue, setError, reset } = methods

	useEffect(() => {
		if (!isOpen) return

		setValue(
			'plan',
			parsePlanName(currentPlan?.name ?? currentUser?.plan?.name),
		)
		setValue('period', currentPlan?.paymentPeriod ?? 'month')
		setValue('business_users', getDefaultUsers('Business'))
		setValue('enterprise_users', getDefaultUsers('Enterprise'))
	}, [currentPlan, currentUser?.plan, isOpen, setValue, purchasedMaxUsers])

	const { period, plan, business_users, enterprise_users } = watch()

	const { mutateAsync: subscribe, isPending } = useSubscribe()

	const handleClose = () => {
		onClose()
		reset()
	}

	const handleConfirm = async (confirm?: boolean) => {
		if (isLoadingPlans || isPricingError) return
		const isLockedPlan = currentUser?.organization?.id
			? plan === 'Free' || plan === 'Pro'
			: plan === 'Business' || plan === 'Enterprise'
		if (isLockedPlan) return

		const users =
			plan === 'Pro'
				? 1
				: plan === 'Business'
					? business_users
					: enterprise_users

		if (plan === 'Business' || plan === 'Enterprise') {
			const fieldName =
				plan === 'Business' ? 'business_users' : 'enterprise_users'
			const usersError = getUsersValidationError(
				plan,
				users,
				currentUser?.plan?.totalUsers,
				parsePlanName(currentPlan?.name) === plan
					? currentPlan?.minUsers
					: undefined,
			)

			if (usersError) {
				setError(
					fieldName,
					{
						type: 'min',
						message: usersError,
					},
					{ shouldFocus: true },
				)
				return
			}
		}

		if (!!isPlanDowngrade(plan, currentPlan) && !confirm) {
			setIsDowngradeSubscriptionModalOpen(true)
			return
		}
		isDowngradeSubscriptionModalOpen &&
			setIsDowngradeSubscriptionModalOpen(false)
		if (isAuthenticated) {
			const selectedModalPlan = modalPlans.find((item) => item.name === plan)
			const selectedPrice = selectedModalPlan
				? getPriceForPeriod(selectedModalPlan.prices, period)
				: undefined

			if (!selectedPrice) return

			await subscribe(
				{
					priceId: selectedPrice.id,
					planName: plan,
					paymentPeriod: period,
					currency: selectedPrice.currency,
					users,
				},
				{
					onSuccess: ({ data }) => {
						if (data?.data?.url) {
							window.open(data?.data?.url, '_blank')
						} else {
							onActionCallback?.()
						}
						handleClose()
					},
				},
			)
		}
	}

	const handleConfirmClick = () => {
		const currentUsers =
			plan === 'Pro'
				? 1
				: plan === 'Business'
					? business_users
					: enterprise_users

		if (currentUsers >= 100) {
			setIsOverLimitModalOpen(true)
			return
		}
		handleConfirm()
	}

	return (
		<FormProvider {...methods}>
			<Fragment>
				<Dialog
					isOpen={isOpen}
					onClose={handleClose}
					title="Choose your plan"
					contentClassName=""
					titleClassName="text-2xl font-bold"
					className="w-[calc(100vw-100px)] max-w-[1350px] h-fit"
					controllers={
						<ModalActions
							currentPlan={currentPlan}
							currentUser={currentUser}
							modalPlans={modalPlans}
							isPending={isPending}
							onCancel={handleClose}
							onConfirm={handleConfirmClick}
						/>
					}
				>
					<div>
						<PeriodSwitcher />

						{isLoadingPlans ? (
							<div className="h-full px-5 py-8 flex flex-col lg:flex-row items-start justify-start w-full gap-5 box-border">
								{Array.from({ length: 4 }).map((_, index) => (
									<div
										key={index}
										className={cn(
											'border rounded-lg w-full lg:w-1/4 shadow-xl relative h-[580px] box-border overflow-hidden',
											'border-stroke-dark dark:border-stroke-light-dark',
											'bg-gradient-to-r from-stroke-light via-background-light-hover to-stroke-light',
											'dark:from-background-dark-light dark:via-background-dark dark:to-background-dark-light',
											'bg-[length:200%_100%] animate-shimmer dark:animate-shimmer-slow',
										)}
									/>
								))}
							</div>
						) : isPricingError ? (
							<div
								className="flex flex-col items-center gap-3 px-5 py-8"
								role="alert"
							>
								<p>Unable to load regional pricing. Please try again.</p>
								<Button
									label="Retry"
									onClick={() => { void refetchPlans() }}
								/>
							</div>
						) : (
							<Controller
								control={methods.control}
								name="plan"
								render={({ field: { value, onChange } }) => (
									<div className="h-full px-5 py-8 flex flex-col lg:flex-row items-start justify-start w-full gap-5 box-border">
										{modalPlans.map((planProduct) => (
											<PlanCard
												key={planProduct.id}
												plan={planProduct}
												isActive={planProduct?.name === value}
												currentPlan={currentPlan}
												currentUser={currentUser}
												period={period}
												onPlanChange={onChange}
												onClose={handleClose}
												onConfigurePlan={onConfigurePlan}
											/>
										))}
									</div>
								)}
							/>
						)}
					</div>
				</Dialog>

				<ConfirmationModal
					className="p-5 w-[440px] box-border"
					isOpen={isDowngradeSubscriptionModalOpen}
					onClose={() => setIsDowngradeSubscriptionModalOpen(false)}
					onAction={() => handleConfirm(true)}
					icon={<InfoOutlined />}
					title="Subscription Downgrade"
					description="Are you sure you want to downgrade your subscription? Some features and benefits will no longer be available after the downgrade and will be deleted permanently."
					actionLabel="Confirm"
					cancelLabel="Cancel"
				/>

				<ConfirmationModal
					className="p-5 w-[440px] box-border"
					isOpen={isOverLimitModalOpen}
					onClose={() => setIsOverLimitModalOpen(false)}
					onAction={() => {
						handleConfirm(true)
						setIsOverLimitModalOpen(false)
					}}
					icon={<Warning className="fill-accent" />}
					title="Warning"
					description={
						<p className="text-base">
							<p>
								You have selected <span className="font-medium">100 users</span>{' '}
								or more.
							</p>
							<p>
								To continue and proceed to payment, you must get approval from
								the <span className="font-bold">Solarion Administrator</span>.
							</p>
						</p>
					}
					actionLabel="Confirm"
					cancelLabel="Cancel"
				/>
			</Fragment>
		</FormProvider>
	)
}

export default ChoosePlanModal
